/**
 * New Platform Event Modal
 *
 * Shared modal for creating a platform event — either linked to an existing
 * HubSpot event or creating a new HubSpot event. Used by the admin Events
 * panel and the campaign channel-creation flow.
 *
 * onCreated receives the created event row so callers can use its id/name.
 *
 * @story SCRUM-1609
 */

import React, { useState, useEffect } from 'react';
import apiRequest from '../../services/api';

const styles = {
  btn: {
    padding: '8px 16px', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontWeight: '500', fontSize: '14px',
  },
  btnPrimary: { backgroundColor: '#3b82f6', color: '#fff' },
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    width: '480px', maxHeight: '90vh', overflowY: 'auto',
  },
  fieldGroup: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '13px', color: '#374151' },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
  },
  error: { color: '#dc2626', fontSize: '13px', marginTop: '4px' },
};

export default function NewEventModal({ onClose, onCreated }) {
  const [markets, setMarkets] = useState([]);
  const [mode, setMode] = useState('select'); // 'select' or 'create'
  const [form, setForm] = useState({
    market_id: '', hubspot_event_id: '', name: '',
    location_name: '', location_address: '',
    starts_at: '', ends_at: '', capacity: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // HubSpot search state
  const [hsSearch, setHsSearch] = useState('');
  const [hsEvents, setHsEvents] = useState([]);
  const [hsLoading, setHsLoading] = useState(false);
  const [hsSelected, setHsSelected] = useState(null);
  const searchTimeoutRef = React.useRef(null);

  useEffect(() => {
    apiRequest('/api/v1/admin/markets')
      .then((res) => {
        if (res.success) { setMarkets(res.data); }
      })
      .catch(() => {});
  }, []);

  // Load initial HubSpot events when mode is 'select'
  useEffect(() => {
    if (mode === 'select') {
      setHsLoading(true);
      apiRequest('/api/v1/admin/events/hubspot-search')
        .then((res) => {
          if (res.success) { setHsEvents(res.events || []); }
        })
        .catch(() => {})
        .finally(() => setHsLoading(false));
    }
  }, [mode]);

  const handleHsSearch = (value) => {
    setHsSearch(value);
    if (searchTimeoutRef.current) { clearTimeout(searchTimeoutRef.current); }
    searchTimeoutRef.current = setTimeout(async () => {
      setHsLoading(true);
      try {
        const res = await apiRequest(`/api/v1/admin/events/hubspot-search?q=${encodeURIComponent(value)}`);
        if (res.success) { setHsEvents(res.events || []); }
      } catch {
        // ignore
      } finally {
        setHsLoading(false);
      }
    }, 400);
  };

  const selectHubspotEvent = (ev) => {
    setHsSelected(ev);
    const toLocal = (iso) => {
      if (!iso) { return ''; }
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setForm((prev) => ({
      ...prev,
      hubspot_event_id: ev.hubspot_event_id,
      name: ev.name || prev.name,
      starts_at: ev.starts_at ? toLocal(ev.starts_at) : prev.starts_at,
      ends_at: ev.ends_at ? toLocal(ev.ends_at) : prev.ends_at,
    }));
    setError('');
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCreate = mode === 'create';

    if (!form.market_id || !form.name || !form.starts_at || !form.ends_at) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!isCreate && !form.hubspot_event_id) {
      setError('Please select a HubSpot event.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const body = {
        market_id: form.market_id,
        name: form.name,
        location_name: form.location_name || undefined,
        location_address: form.location_address || undefined,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
        capacity: form.capacity ? parseInt(form.capacity, 10) : undefined,
      };

      if (isCreate) {
        body.create_in_hubspot = true;
      } else {
        body.hubspot_event_id = form.hubspot_event_id;
      }

      const res = await apiRequest('/api/v1/admin/events', { method: 'POST', body });
      if (res.success) {
        onCreated(res.data);
      }
    } catch (err) {
      const msg = err?.message || err?.error || 'Failed to create event';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const formatHsDate = (iso) => {
    if (!iso) { return ''; }
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: '18px' }}>New Platform Event</h2>
        <form onSubmit={handleSubmit}>

          {/* Mode toggle */}
          <div style={{ ...styles.fieldGroup, display: 'flex', gap: '16px' }}>
            <label style={{ fontSize: '13px', cursor: 'pointer' }}>
              <input type="radio" value="select" checked={mode === 'select'} onChange={() => { setMode('select'); setHsSelected(null); }} />{' '}
              Link to existing HubSpot event
            </label>
            <label style={{ fontSize: '13px', cursor: 'pointer' }}>
              <input type="radio" value="create" checked={mode === 'create'} onChange={() => setMode('create')} />{' '}
              Create new HubSpot event
            </label>
          </div>

          {/* HubSpot event selector (select mode) */}
          {mode === 'select' && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>HubSpot Event *</label>
              <input
                style={styles.input}
                placeholder="Search HubSpot events..."
                value={hsSearch}
                onChange={(e) => handleHsSearch(e.target.value)}
              />
              {hsLoading && <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0' }}>Searching...</p>}
              {!hsLoading && hsEvents.length === 0 && (
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0' }}>No HubSpot events found.</p>
              )}
              {hsEvents.length > 0 && !hsSelected && (
                <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, border: '1px solid #e5e7eb', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                  {hsEvents.map((ev) => (
                    <li
                      key={ev.hubspot_event_id}
                      style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}
                      onClick={() => selectHubspotEvent(ev)}
                    >
                      <strong>{ev.name}</strong>
                      {ev.starts_at && <span style={{ color: '#6b7280' }}> — {formatHsDate(ev.starts_at)}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {hsSelected && (
                <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '6px', fontSize: '13px' }}>
                  Selected: <strong>{hsSelected.name}</strong> ({hsSelected.hubspot_event_id})
                  <button
                    type="button"
                    style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '12px' }}
                    onClick={() => { setHsSelected(null); setForm((f) => ({ ...f, hubspot_event_id: '' })); }}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Market *</label>
            <select style={styles.input} value={form.market_id} onChange={handleChange('market_id')}>
              <option value="">Select market...</option>
              {markets.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Name *</label>
            <input style={styles.input} value={form.name} onChange={handleChange('name')} />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Location Name</label>
            <input style={styles.input} value={form.location_name} onChange={handleChange('location_name')} />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Location Address</label>
            <input style={styles.input} value={form.location_address} onChange={handleChange('location_address')} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...styles.fieldGroup, flex: 1 }}>
              <label style={styles.label}>Starts At *</label>
              <input style={styles.input} type="datetime-local" value={form.starts_at} onChange={handleChange('starts_at')} />
            </div>
            <div style={{ ...styles.fieldGroup, flex: 1 }}>
              <label style={styles.label}>Ends At *</label>
              <input style={styles.input} type="datetime-local" value={form.ends_at} onChange={handleChange('ends_at')} />
            </div>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Capacity</label>
            <input style={styles.input} type="number" min="1" value={form.capacity} onChange={handleChange('capacity')} />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button type="button" style={{ ...styles.btn, backgroundColor: '#f3f4f6', color: '#374151' }} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary }} disabled={saving}>
              {saving ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
